"use client";

import {
  walletTelemetry,
  telemetryAttemptId,
  type WalletTelemetryInput,
} from "@mudwater/sdk";

import {
  endIdentityTrace,
  identityTracePhase,
} from "@/lib/wallet/identity-trace";
import { rumError, rumEvent } from "@/lib/faro";
import {
  identityAttempt,
  identityFailure,
} from "@/lib/wallet/identity-diagnostics";
import { errorCode } from "@/lib/flow-trace";
import { safeText } from "@/lib/wallet/solana/display";
import {
  isUntrusted,
  reportedError,
  traceText,
} from "@/lib/wallet/untrusted-text";

import type { IdentityAttempt } from "@/lib/wallet/identity-diagnostics";

/**
 * The caught error as telemetry may carry it, for a wallet whose text is not
 * ours to trust (an unknown Wallet Standard wallet can reject with any text
 * it likes): its message and code sanitised and capped, and an error whose
 * message had to change replaced by a clean copy, its name kept (sanitised
 * too). Raw causes are omitted because Faro serializes them.
 */
function reportable(error: unknown): {
  error: unknown;
  code: string;
  message: string;
} {
  const code = safeText(errorCode(error), 64);
  if (!(error instanceof Error)) return { error, code, message: "" };
  const message = safeText(error.message, 200);
  const clean = new Error(message);
  clean.name = safeText(error.name, 64);
  return { error: clean, code, message };
}

/**
 * Wallet connection is the gate in front of every precious path, so each
 * attempt is a `wallet_connect_started` event followed by `_finished` or
 * `_failed` (with its code and the caught error). Glyph finishes when its
 * login modal opens; `wallet_connected` marks the address actually arriving.
 * A connect that left the page for a wallet's app (`"redirected"`) ends in
 * `_redirected` instead, so it is never counted as a connection.
 *
 * `untrusted` (the generic Solana bridge only) sanitises what is reported.
 * Without it the error goes to Faro exactly as it was caught, as it always
 * has: the EVM, Phantom and Glyph bridges keep their stack and cause chain
 * (final review, M1).
 */
export function trackedConnect<T>(
  wallet: string,
  connect: () => Promise<T>,
  options: { untrusted?: boolean; solana?: WalletTelemetryInput } = {}
): () => Promise<T> {
  return async () => {
    const diagnostics = {
      ...(options.solana ? walletTelemetry(options.solana) : {}),
      attempt_id: telemetryAttemptId(),
    };
    const started = performance.now();
    const ms = () => String(Math.round(performance.now() - started));
    emit("wallet_connect_started", {
      ...diagnostics,
      wallet,
      phase: "connect",
    });
    try {
      const result = await connect();
      emit(
        result === "redirected"
          ? "wallet_connect_redirected"
          : "wallet_connect_finished",
        { ...diagnostics, wallet, ms: ms() }
      );
      return result;
    } catch (error) {
      const failure = identityFailure(error);
      let report = {
        error: options.untrusted
          ? new Error("The wallet refused the request.")
          : error,
        code: failure.error_code ?? "unknown",
        message: "",
      };
      try {
        report = options.untrusted
          ? reportable(error)
          : {
              error,
              code: errorCode(error),
              message:
                error instanceof Error ? error.message.slice(0, 200) : "",
            };
      } catch {
        // Error getters and telemetry cannot replace the original rejection.
      }
      try {
        rumError(report.error, {
          ...diagnostics,
          flow: "wallet_connect",
          wallet,
          code: report.code,
          ...failure,
        });
      } catch {
        // Continue with the bounded event even if the error exporter fails.
      }
      emit("wallet_connect_failed", {
        ...diagnostics,
        wallet,
        code: report.code,
        message: report.message,
        ms: ms(),
        phase: "connect",
        ...failure,
      });
      throw error;
    }
  };
}

/**
 * A sign-in that failed for any reason but the holder declining. An unknown
 * wallet's error is sanitised (`untrusted-text.ts`); every other is reported
 * as caught.
 */
export function signInFailed(
  route: string,
  error: unknown,
  attempt?: IdentityAttempt
): void {
  const owned = attempt ?? identityAttempt(route);
  const fields: Record<string, string> = {
    ...owned,
    route,
    ...identityFailure(error),
  };
  let code = fields.error_code ?? "unknown";
  try {
    code = traceText(errorCode(error), isUntrusted(error), 64);
    rumError(reportedError(error), { ...fields, flow: "wallet_sign_in" });
  } catch {
    // Untrusted getters or an unavailable collector must not affect sign-in.
  }
  identityTracePhase(owned, "failed", fields);
  emit("wallet_sign_in_failed", { ...fields, code });
  if (!attempt) endIdentityTrace(owned);
}

function emit(name: string, fields: Record<string, string>): void {
  try {
    rumEvent(name, fields);
  } catch {
    // Telemetry never prevents or replaces a wallet operation.
  }
}
